import { useState, useMemo, useCallback, useEffect } from 'react';
import {
  Box,
  Stack,
  Card,
  CardContent,
  Typography,
  Chip,
  Paper,
  TextField,
  Button,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  Checkbox,
  Grid,
  ListItemText,
  Tooltip,
  CircularProgress
} from '@mui/material';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import AccountBoxOutlinedIcon from '@mui/icons-material/AccountBoxOutlined';
import SearchOffIcon from '@mui/icons-material/SearchOff';
// import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import LocalPhoneIcon from '@mui/icons-material/LocalPhone';
import SlowMotionVideoOutlinedIcon from '@mui/icons-material/SlowMotionVideoOutlined';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../../utils/i18n';
import { apiVss } from '../../../api/axiosClient';
import VoiceCallDialog from './VoiceCallDialog';
import { API_BASES } from '../../../config/constants';
import { getHowenToken } from '../../../utils/vssHelper';

const getDeviceStatusInfo = (accessMode) => {
  if (accessMode === undefined || accessMode === null) return { color: 'disabled', tooltip: 'Checking Status...' };
  switch (Number(accessMode)) {
    case -1:
      return { color: 'error', tooltip: 'Offline' };
    case 0:
      return { color: 'disabled', tooltip: 'Unknown' };
    case 1:
      return { color: '#01FF05', tooltip: 'Online (Ethernet)' };
    case 2:
      return { color: '#01FF05', tooltip: 'Online (WIFI)' };
    case 3:
      return { color: '#01FF05', tooltip: 'Online (2G)' };
    case 4:
      return { color: '#01FF05', tooltip: 'Online (3G)' };
    case 5:
      return { color: '#01FF05', tooltip: 'Online (4G)' };
    case 6:
      return { color: '#01FF05', tooltip: 'Online (5G)' };
    case 7:
      return { color: '#01FF05', tooltip: 'Online (WIFI+3/4/5G)' };
    case 8:
      return { color: '#01FF05', tooltip: 'Online (CABLE+3/4/5G)' };
    default:
      return { color: 'disabled', tooltip: 'Unknown' };
  }
};

const statusColor = (rawVehicle, isLoaded = false) => {
  if (!rawVehicle) return 'default';
  const data = rawVehicle.raw || rawVehicle;
  const status = (data.status || rawVehicle.status || '').toString().toLowerCase();
  const engine = (data.engine || rawVehicle.engine || '').toString().toUpperCase();
  const speed = Number(data.speed || rawVehicle.speed) || 0;
  const gps = (data.gps || rawVehicle.gps || '').toString().toLowerCase();

  const isAtPolygon =
    status.includes('at pool') || status.includes('at geofence') || status.includes('at customer') || status.includes('at polygon');
  const isOverSpeedSpatial = data.spatial_id > 0 && data.spatial_max_speed > 0 && speed > data.spatial_max_speed && isLoaded;
  const isUnderSpeedSpatial = data.spatial_id > 0 && data.spatial_min_speed > 0 && speed < data.spatial_min_speed && isLoaded;
  const isStatusOverSpeed = status.includes('over speed') || status.includes('overspeed');
  const isStatusUnderSpeed = status.includes('under speed') || status.includes('underspeed');
  const isGpsUpdated = gps === 'update' || gps === 'updated';

  if ((isStatusOverSpeed || isOverSpeedSpatial) && isGpsUpdated) return 'success';
  if ((isStatusUnderSpeed || isUnderSpeedSpatial) && isGpsUpdated) return 'success';

  if (status.includes('delay') || !isGpsUpdated) return 'error';
  if (status.includes('maintenance')) return 'error';

  if ((engine === 'ON' || engine === 'IDLE') && speed > 0 && isGpsUpdated) return 'success';
  if ((engine === 'ON' || engine === 'IDLE') && speed === 0 && isGpsUpdated) return 'warning';

  if (engine === 'OFF') {
    if (isAtPolygon) return 'primary';
    return 'default';
  }

  if (speed > 0 && isGpsUpdated) return 'success';
  if (isAtPolygon) return 'primary';

  return 'default';
};

const getStatusLabel = (rawVehicle, isLoaded = false) => {
  if (!rawVehicle) return '-';
  const data = rawVehicle.raw || rawVehicle;
  const status = (data.status || rawVehicle.status || '').toString().toLowerCase();
  const engine = (data.engine || rawVehicle.engine || '').toString().toUpperCase();
  const speed = Number(data.speed || rawVehicle.speed) || 0;
  const gps = (data.gps || rawVehicle.gps || '').toString().toLowerCase();

  const isAtPolygon =
    status.includes('at pool') || status.includes('at geofence') || status.includes('at customer') || status.includes('at polygon');
  const isOverSpeedSpatial = data.spatial_id > 0 && data.spatial_max_speed > 0 && speed > data.spatial_max_speed && isLoaded;
  const isUnderSpeedSpatial = data.spatial_id > 0 && data.spatial_min_speed > 0 && speed < data.spatial_min_speed && isLoaded;
  const isStatusOverSpeed = status.includes('over speed') || status.includes('overspeed');
  const isStatusUnderSpeed = status.includes('under speed') || status.includes('underspeed');
  const isGpsUpdated = gps === 'update' || gps === 'updated';

  if ((isStatusOverSpeed || isOverSpeedSpatial) && isGpsUpdated) return 'Moving';
  if ((isStatusUnderSpeed || isUnderSpeedSpatial) && isGpsUpdated) return 'Moving';

  if (gps.includes('delay')) {
    if (gps.includes('24')) return 'Delay > 24h';
    if (gps.includes('1')) return 'Delay > 1h';
    return 'Delay';
  }
  if (status.includes('delay')) {
    if (status.includes('24')) return 'Delay > 24h';
    if (status.includes('1')) return 'Delay > 1h';
    return 'Delay';
  }
  if (!isGpsUpdated) {
    return 'Delay';
  }
  if (status.includes('maintenance')) return 'Maintenance';

  if ((engine === 'ON' || engine === 'IDLE') && speed > 0 && isGpsUpdated) return 'Moving';
  if ((engine === 'ON' || engine === 'IDLE') && speed === 0 && isGpsUpdated) return 'Idle';

  if (engine === 'OFF') {
    if (isAtPolygon) return 'Parked';
    return 'Stopped';
  }

  if (speed > 0 && isGpsUpdated) return 'Moving';
  if (isAtPolygon) return 'Parked';

  return 'Stopped';
};

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 180
    }
  }
};

function VehicleCard({
  v,
  onChangeSelected,
  onManualPlay,
  onOpenVoiceCall,
  vehicleList,
  activeStream,
  deviceStatusMap,
  devicesLoading,
  installedCameras = []
}) {
  const { t } = useLanguage();
  const cameraOptions = useMemo(() => {
    const imei = String(v.integration_imei || (v.raw && v.raw.integration_imei) || (v.info && v.info.integration_imei) || '').trim();
    if (!imei || imei === 'Camera not installed' || imei === 'null' || imei === 'undefined') return [];

    const installed = installedCameras.find((c) => String(c.deviceno) === imei);
    if (installed && installed.channelname) {
      const channels = installed.channelname.split(';').filter((ch) => ch.trim() !== '');
      if (channels.length > 0) {
        return channels.map((ch, index) => {
          const num = ch.replace(/\D/g, '') || index + 1;
          return { value: `camera_${num}`, label: `Camera ${num}` };
        });
      }
    }
    return [
      { value: 'camera_1', label: 'Camera 1' },
      { value: 'camera_2', label: 'Camera 2' },
      { value: 'camera_3', label: 'Camera 3' },
      { value: 'camera_4', label: 'Camera 4' }
    ];
  }, [installedCameras, v]);

  const activeChannels = activeStream?.chs ? activeStream.chs.split('_') : [];
  const isActive = activeChannels.length > 0;

  const [selectedCameras, setSelectedCameras] = useState([]);

  useEffect(() => {
    if (activeStream?.chs) {
      const playingCams = activeStream.chs.split('_').map((ch) => `camera_${ch}`);
      setSelectedCameras(playingCams);
      if (typeof onChangeSelected === 'function') {
        onChangeSelected(String(v.id), playingCams);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStream?.chs, v.id]);

  const handleCameraChange = (event) => {
    const value = event.target.value;
    if (value.includes('all')) {
      if (selectedCameras.length === cameraOptions.length) {
        setSelectedCameras([]);
        if (typeof onChangeSelected === 'function') onChangeSelected(String(v.id), []);
      } else {
        const allCams = cameraOptions.map((o) => String(o.value));
        setSelectedCameras(allCams);
        if (typeof onChangeSelected === 'function') onChangeSelected(String(v.id), allCams);
      }
    } else {
      const arr = Array.isArray(value) ? value.map((x) => String(x)).filter((x) => x !== 'all') : [String(value)];
      setSelectedCameras(arr);
      if (typeof onChangeSelected === 'function') onChangeSelected(String(v.id), arr);
    }
  };

  const renderSelectedCameras = (selected) => {
    const selectedArr = Array.isArray(selected) ? selected : [];
    if (selectedArr.length === 0) return '—';
    const filtered = selectedArr.filter((x) => String(x) !== 'all');
    if (filtered.length === 0) return '—';
    const visible = filtered.slice(0, 1);
    const overflow = Math.max(0, filtered.length - visible.length);
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {visible.map((val) => {
          const found = cameraOptions.find((o) => String(o.value) === String(val));
          return <Chip key={val} size="small" label={found ? found.label : String(val)} />;
        })}
        {overflow > 0 && (
          <Button
            size="small"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
            }}
            sx={{ ml: 0.5, minWidth: 'auto', p: '4px 8px' }}
          >
            +{overflow} more
          </Button>
        )}
      </Box>
    );
  };

  const navigateto = useNavigate();
  const handleShowHistoryvideo = useCallback(() => {
    navigateto('/history-video?name=history-video', {
      state: {
        vehicle: v,
        vehicleList: vehicleList
      }
    });
  }, [navigateto, v, vehicleList]);

  const handleCardClick = () => {
    if (typeof onManualPlay === 'function') onManualPlay(v);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleCardClick();
    }
  };

  return (
    <Card
      role="button"
      tabIndex={0}
      onClick={handleCardClick}
      onKeyDown={handleKeyDown}
      variant="outlined"
      sx={{
        borderRadius: 2,
        border: 1,
        borderColor: isActive ? '#dc3232' : '#485D9A',
        boxShadow: '0 6px 18px rgba(8,14,30,0.04)',
        cursor: 'pointer',
        position: 'relative',
        overflow: 'hidden',
        '&:hover': { boxShadow: '0 10px 24px rgba(8,14,30,0.08)' }
      }}
    >
      <CardContent sx={{ py: 1.25, px: 1.5, position: 'relative' }}>
        {isActive && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              right: 0,
              bgcolor: '#dc3232',
              color: '#fff',
              px: 1,
              py: 0.2,
              borderBottomLeftRadius: 8,
              fontSize: '0.65rem',
              fontWeight: 'bold',
              zIndex: 1,
              boxShadow: 1
            }}
          >
            LIVE: CH {activeChannels.join(', ')}
          </Box>
        )}
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: isActive ? 2 : 0 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }} noWrap>
              {v.fleet_name}
            </Typography>
          </Box>
          <Box spacing={1} alignItems="flex-end">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {(() => {
                const imei = String(
                  v.integration_imei || (v.raw && v.raw.integration_imei) || (v.info && v.info.integration_imei) || ''
                ).trim();
                if (!imei || imei === 'Camera not installed') return null;
                const statusData = deviceStatusMap?.[imei];
                if (devicesLoading && !statusData) {
                  return (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 24, height: 24 }}>
                      <CircularProgress size={16} />
                    </Box>
                  );
                }
                const accessMode = statusData?.accessMode;
                const statusInfo = getDeviceStatusInfo(accessMode);
                const titleNode = (
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      {statusInfo.tooltip}
                    </Typography>
                    {statusData?.lastOnlineTime && (
                      <Typography variant="caption" display="block">
                        Last Online: {statusData.lastOnlineTime}
                      </Typography>
                    )}
                    {statusData?.lastOfflineTime && (
                      <Typography variant="caption" display="block">
                        Last Offline: {statusData.lastOfflineTime}
                      </Typography>
                    )}
                  </Box>
                );
                return (
                  <Tooltip title={titleNode}>
                    <Box>
                      <VideocamOutlinedIcon sx={{ color: statusInfo.color, fontWeight: 700 }} />
                    </Box>
                  </Tooltip>
                );
              })()}
              <Chip label={getStatusLabel(v)} variant="outlined" color={statusColor(v)} size="small" />
            </Box>
          </Box>
        </Stack>

        <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center">
          <Box>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
              <Tooltip title="Vehicle Type">
                <LocalShippingOutlinedIcon fontSize="small" />
              </Tooltip>
              <Typography variant="body2" noWrap>
                {v.vehicle_type ?? '—'}
              </Typography>
            </Stack>
          </Box>
          <Box>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
              <Tooltip title="Driver Name">
                <AccountBoxOutlinedIcon fontSize="small" />
              </Tooltip>
              <Typography variant="body2" noWrap>
                {v.driver_name ?? '—'}
              </Typography>
            </Stack>
          </Box>
        </Stack>
        <Grid container spacing={1} size={{ sm: 12 }} mt={2}>
          {cameraOptions.length > 0 && (
            <Grid size={{ sm: 8, xs: 8 }}>
              <FormControl onClick={(e) => e.stopPropagation()} fullWidth>
                <InputLabel id={`camera-select-label-${v.id}`}>{t('liveStreaming.selectCamera', 'Pilih Camera')}</InputLabel>
                <Select
                  labelId={`camera-select-label-${v.id}`}
                  multiple
                  value={selectedCameras}
                  onChange={handleCameraChange}
                  input={<OutlinedInput label={t('liveStreaming.selectCamera', 'Pilih Camera')} />}
                  renderValue={(selected) => renderSelectedCameras(selected)}
                  MenuProps={MenuProps}
                  size="small"
                >
                  <MenuItem value="all">
                    <Checkbox
                      checked={selectedCameras.length > 0 && selectedCameras.length === cameraOptions.length}
                      indeterminate={selectedCameras.length > 0 && selectedCameras.length < cameraOptions.length}
                    />
                    <ListItemText primary="Select All" />
                  </MenuItem>
                  {cameraOptions.map((opt) => {
                    const val = String(opt.value);
                    return (
                      <MenuItem key={val} value={val}>
                        <Checkbox checked={selectedCameras.indexOf(val) > -1} />
                        <ListItemText primary={opt.label} />
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Grid>
          )}
          <Grid size={{ sm: cameraOptions.length > 0 ? 4 : 12, xs: cameraOptions.length > 0 ? 4 : 12 }}>
            <Box sx={{ display: 'flex', justifyContent: cameraOptions.length > 0 ? 'end' : 'end', alignItems: 'center', gap: 1 }}>
              <Tooltip title="Panggilan Suara">
                <Button
                  variant="outlined"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (typeof onOpenVoiceCall === 'function') onOpenVoiceCall(v);
                  }}
                  sx={{
                    width: 32,
                    height: 32,
                    minWidth: 32,
                    borderRadius: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 0,
                    borderColor: '#485D9A',
                    '&:hover': { borderColor: '#485D9A' }
                  }}
                >
                  <LocalPhoneIcon sx={{ fontSize: 24, color: '#485D9A', fontWeight: 700 }} />
                </Button>
              </Tooltip>
              <Tooltip title="Riwayat Video">
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleShowHistoryvideo();
                  }}
                  variant="outlined"
                  sx={{
                    width: 32,
                    height: 32,
                    minWidth: 32,
                    borderRadius: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 0,
                    borderColor: '#485D9A',
                    '&:hover': { borderColor: '#485D9A' }
                  }}
                >
                  <SlowMotionVideoOutlinedIcon sx={{ fontSize: 24, color: '#485D9A', fontWeight: 700 }} />
                </Button>
              </Tooltip>
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
}
export default function StreamingInfo({ vehicleList = [], onSelectStream, activeStreams = [] }) {
  const { t } = useLanguage();
  const [query, setQuery] = useState('');
  const [searchBy, setSearchBy] = useState('vehicle');
  const [token, setToken] = useState('');
  const [tokenLoading, setTokenLoading] = useState(false);
  const [selectedMap, setSelectedMap] = useState({});
  const [deviceStatusMap, setDeviceStatusMap] = useState({});
  const [devicesLoading, setDevicesLoading] = useState(false);
  const [installedCameras, setInstalledCameras] = useState([]);
  const navigate = useNavigate();
  const [openVoiceCall, setOpenVoiceCall] = useState(false);
  const [selectedVoiceCallVehicle, setSelectedVoiceCallVehicle] = useState(null);

  const handleOpenVoiceCall = (vehicle) => {
    setSelectedVoiceCallVehicle(vehicle);
    setOpenVoiceCall(true);
  };

  useEffect(() => {
    if (!token) return;
    try {
      const cached = localStorage.getItem('vehicle_installed_camera');
      if (cached) {
        setInstalledCameras(JSON.parse(cached));
        return;
      }
    } catch (e) {
      console.error('Error parsing cached cameras', e);
    }

    const imeiArray = (vehicleList || [])
      .map((v) => v.integration_imei || (v.raw && v.raw.integration_imei) || (v.info && v.info.integration_imei))
      .filter((imei) => imei && String(imei).trim() !== '' && String(imei).trim() !== 'Camera not installed');

    const uniqueImeis = [...new Set(imeiArray)];
    if (uniqueImeis.length === 0) return;

    Promise.all(
      uniqueImeis.map((imei) => {
        const params = new URLSearchParams();
        params.append('pageNum', '-1');
        params.append('pageCount', '-1');
        params.append('token', String(token));
        params.append('scheme', 'https');
        params.append('lang', 'en_US');
        params.append('keyword', imei);

        return apiVss
          .post('/vehicle/findAll.action', params, {
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
          })
          .then((resp) => {
            const responseData = resp.data?.data || resp.data;
            const dataList = responseData?.dataList || responseData;
            if (Array.isArray(dataList)) {
              return dataList.map((item) => ({
                deviceno: item.deviceno || item.imei,
                channelname: item.channelname
              }));
            }
            return [];
          })
          .catch((err) => {
            console.error(`Failed to fetch cameras for ${imei}`, err);
            return [];
          });
      })
    ).then((results) => {
      const combined = results.flat();
      if (combined.length > 0) {
        localStorage.setItem('vehicle_installed_camera', JSON.stringify(combined));
        setInstalledCameras(combined);
      }
    });
  }, [token, vehicleList]);

  useEffect(() => {
    if (!token || !vehicleList || vehicleList.length === 0) return;
    const imeiList = vehicleList
      .map((v) => v.integration_imei || (v.raw && v.raw.integration_imei) || (v.info && v.info.integration_imei))
      .filter((imei) => imei && String(imei).trim() !== '' && String(imei).trim() !== 'Camera not installed')
      .join(',');

    if (!imeiList) return;

    setDevicesLoading(true);
    apiVss
      .post('/onoffline/queryMoreDeviceStatus.action', {
        token: String(token),
        deviceID: imeiList
      })
      .then((resp) => {
        const data = resp.data?.data || resp.data || [];
        const map = {};
        if (Array.isArray(data)) {
          data.forEach((item) => {
            const id = item.deviceID || item.id || item.deviceID;
            if (id) map[id] = item; // Store full object for lastOnlineTime
          });
        }
        setDeviceStatusMap(map);
      })
      .catch((err) => console.error('Failed to fetch device status', err))
      .finally(() => setDevicesLoading(false));
  }, [token, vehicleList]);

  const mapped = useMemo(() => {
    return (vehicleList || []).map((v) => {
      const id = v.vehicle ?? v.info?.id ?? v.id ?? `${v.vehicle_license_plate ?? 'unknown'}-${v.integration__no ?? ''}`;
      const fleet_name = v.vehicle_license_plate || v.info?.name || 'Unknown';
      const status = v.status || v.info?.status_text || 'unknown';
      const vehicle_type = v.vehicle_type_name || v.info?.vehicle_type_name || v.vehicle_type || '-';
      const driver_name = v.driver_name || v.join?.driver_name || v.info?.driver_name || '-';
      const integration_imei =
        (v.integration_imei && String(v.integration_imei).trim()) ||
        (v.info && v.info.integration_imei && String(v.info.integration_imei).trim()) ||
        (v.raw && v.raw.integration_imei && String(v.raw.integration_imei).trim()) ||
        'Camera not installed';
      const vehicle_odometer_km = v.current_odo ?? v.info?.current_odo ?? 0;
      const last_location_time = v.last_update || v.info?.modified_date || '-';
      const last_connected_time = v.last_update || '-';
      const stop_duration = v.stop_duration ?? v.speed_message ?? (v.speed != null ? `${v.speed} Km/H` : '—');
      const engine_status = v.engine ?? v.info?.engine_status ?? v.engine_status ?? '-';
      const distance_traveled_km = v.distance_traveled_km ?? v.info?.total_distance ?? 0;
      const trip_time = v.trip_time ?? '-';
      let fuel_liter = null;
      if (typeof v.fuel === 'number') fuel_liter = v.fuel;
      else if (v.canbus && v.canbus.canFuelPercentage) fuel_liter = v.canbus.canFuelPercentage;
      else if (v.info && v.info.canFuelPercentage) fuel_liter = v.info.canFuelPercentage;
      else fuel_liter = v.fuel ?? (v.info?.fuel_capacity ? `${v.info.fuel_capacity}` : '-');
      const last_location = {
        address: v.street || v.location || `${v.sub_district || ''} ${v.district || ''}`.trim() || '-',
        is_location_updated: (v.gps || '').toString().toLowerCase() === 'update'
      };
      const total_work_hours = v.total_work_hours ?? v.info?.canEngineWorktime ?? '-';
      return {
        id,
        fleet_name,
        status,
        vehicle_type,
        driver_name,
        integration_imei,
        vehicle_odometer_km,
        last_location_time,
        last_connected_time,
        stop_duration,
        engine_status,
        distance_traveled_km,
        trip_time,
        fuel_liter,
        total_work_hours,
        last_location,
        raw: v
      };
    });
  }, [vehicleList]);

  const sortedMapped = useMemo(() => {
    const arr = Array.isArray(mapped) ? mapped.slice() : [];
    arr.sort((a, b) => {
      const ai = String(a.raw?.integration_imei || '').trim();
      const bi = String(b.raw?.integration_imei || '').trim();
      const av = String(a.raw?.integration_vendor || '')
        .trim()
        .toLowerCase();
      const bv = String(b.raw?.integration_vendor || '')
        .trim()
        .toLowerCase();
      const aHas = ai !== '' || av !== '';
      const bHas = bi !== '' || bv !== '';
      if (aHas === bHas) return 0;
      return aHas ? -1 : 1;
    });
    return arr;
  }, [mapped]);

  useEffect(() => {
    let isMounted = true;
    const loadToken = async () => {
      setTokenLoading(true);
      try {
        const fetchedToken = await getHowenToken(10000);
        if (isMounted && fetchedToken) {
          setToken(String(fetchedToken));
        }
      } catch (err) {
        console.warn('[StreamingInfo] fetch token error:', err);
      } finally {
        if (isMounted) setTokenLoading(false);
      }
    };
    loadToken();
    return () => {
      isMounted = false;
    };
  }, []);
  const buildChsFromSelected = (selected = []) => {
    if (!Array.isArray(selected) || selected.length === 0) return '';
    const digits = selected
      .map((s) => {
        const m = String(s).match(/(\d+)/);
        return m ? m[1] : null;
      })
      .filter(Boolean);
    return digits.join('_');
  };
  const buildParams = useCallback(
    (vehicle, selectedCamerasArr) => {
      const deviceId = vehicle?.integration_imei || '';
      const chs = Array.isArray(selectedCamerasArr) && selectedCamerasArr.length > 0 ? buildChsFromSelected(selectedCamerasArr) : '';
      const stream = '0';
      const wnum = String(Math.max(1, selectedCamerasArr?.length || 1));
      const panel = '1';
      const buffer = '2000';
      const baseUrl = `${API_BASES.VSS}/apiPage/RealVideo.html`;
      return {
        token: token || '',
        deviceId,
        chs,
        stream,
        wnum,
        panel,
        buffer,
        baseUrl
      };
    },
    [token]
  );
  const handleChangeSelected = (vehicleId, arr) => {
    setSelectedMap((m) => ({ ...m, [vehicleId]: arr }));
  };

  const handleManualPlay = (vehicle) => {
    const sel = selectedMap[vehicle.id] || [];
    const cameras = sel.length ? sel : ['camera_1'];
    const params = buildParams(vehicle, cameras);

    if (typeof onSelectStream === 'function') {
      onSelectStream({ ...params, vehicle });
    }
  };

  const handleShowHistory = useCallback(() => {
    navigate(`/history-download-video?name=history-download-video`, {
      state: { vehicleList }
    });
  }, [navigate, vehicleList]);

  return (
    <Box sx={{ height: '100%' }}>
      <Paper elevation={2} sx={{ width: 1, height: '100%', display: 'flex', flexDirection: 'column', p: 1, overflow: 'hidden' }}>
        {/* Sticky Header */}
        <Box sx={{ position: 'sticky', top: 0, bgcolor: '#fff', zIndex: 10, pb: 1, borderBottom: '1px solid #e2e8f0' }}>
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'space-between', mt: 1 }}>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Serch By</InputLabel>
              <Select
                value={searchBy}
                label="Search By"
                onChange={(e) => setSearchBy(e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#485D9A' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#485D9A' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#485D9A' }
                }}
              >
                <MenuItem value="vehicle">Vehicle</MenuItem>
                <MenuItem value="driver_name">Driver Name</MenuItem>
                <MenuItem value="vehicle_type">Vehicle Type</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label={t('liveStreaming.searchVehicle', 'Pencarian')}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              size="small"
              fullWidth
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: '#485D9A' },
                  '&:hover fieldset': { borderColor: '#485D9A' },
                  '&.Mui-focused fieldset': { borderColor: '#485D9A' }
                },
                '& .MuiInputLabel-root.Mui-focused': { color: '#485D9A' }
              }}
            />
            {/* <Button
              variant="outlined"
              sx={{
                width: 40,
                height: 40,
                minWidth: 40,
                borderRadius: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: 0,
                borderColor: '#485D9A',
                '&:hover': { borderColor: '#485D9A' }
              }}
            >
              <FilterAltOutlinedIcon sx={{ fontSize: 24, color: '#485D9A', fontWeight: 700 }} />
            </Button> */}
          </Box>
          <Box sx={{ mt: 1 }}>
            <Button
              variant="contained"
              onClick={handleShowHistory}
              size="small"
              sx={{ py: 1, width: 1, bgcolor: '#0374DA', color: '#ffff', fontWeight: 'bold', '&:hover': { bgcolor: '#0889faff' } }}
            >
              {t('liveStreaming.downloadVideoHistory', 'Riwayat Unduhan Video')}
            </Button>
          </Box>
        </Box>

        {/* Scrollable Vehicle List */}
        <Box
          sx={{
            flexGrow: 1,
            mt: 1,
            overflowY: 'auto',
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': {
              display: 'none'
            }
          }}
        >
          <Stack spacing={1} sx={{ pb: 1 }}>
            {(() => {
              const filtered = sortedMapped.filter((m) => {
                if (!query) return true;
                const q = query.toString().trim().toLowerCase();
                let candidates = [];
                if (searchBy === 'vehicle') {
                  candidates = [m.fleet_name, m.raw?.vehicle_license_plate, m.raw?.license_plate, m.raw?.info?.name, m.raw?.vehicle_name];
                } else if (searchBy === 'driver_name') {
                  candidates = [m.driver_name];
                } else if (searchBy === 'vehicle_type') {
                  candidates = [m.vehicle_type];
                }
                candidates = candidates.filter(Boolean).map((s) => String(s).toLowerCase());
                return candidates.some((c) => c.includes(q));
              });

              if (filtered.length === 0) {
                return (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minHeight: '250px',
                      mt: 2,
                      p: 3,
                      mx: 2,
                      textAlign: 'center',
                      bgcolor: 'rgba(72, 93, 154, 0.03)',
                      borderRadius: 1,
                      border: '1px dashed rgba(72, 93, 154, 0.25)',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    <Box
                      sx={{
                        width: 72,
                        height: 72,
                        borderRadius: '50%',
                        bgcolor: '#ffffff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 2.5,
                        boxShadow: '0px 8px 24px rgba(72, 93, 154, 0.12)'
                      }}
                    >
                      <SearchOffIcon sx={{ fontSize: 36, color: '#485D9A', opacity: 0.8 }} />
                    </Box>
                    <Typography variant="subtitle1" sx={{ color: '#485D9A', fontWeight: 700, mb: 1 }}>
                      {t('mapsTracking.vehicleNotFound', 'Kendaraan Tidak Ditemukan')}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.5 }}>
                      {t('mapsTracking.noVehicleMatchKeyword', 'Tidak ada kendaraan yang cocok dengan "')}
                      <b>{query}</b>
                      {t('mapsTracking.pleaseUseAnotherKeyword', '". Silakan gunakan kata kunci lain.')}
                    </Typography>
                  </Box>
                );
              }

              return filtered.map((v) => {
                const activeStream = activeStreams.find((s) => s.vehicle.id === v.id);
                return (
                  <VehicleCard
                    key={v.id}
                    v={v}
                    vehicleList={vehicleList}
                    activeStream={activeStream}
                    onChangeSelected={handleChangeSelected}
                    onManualPlay={handleManualPlay}
                    onOpenVoiceCall={handleOpenVoiceCall}
                    deviceStatusMap={deviceStatusMap}
                    devicesLoading={devicesLoading}
                    installedCameras={installedCameras}
                  />
                );
              });
            })()}
          </Stack>
        </Box>
        {tokenLoading && (
          <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={14} />
            <Typography variant="caption" color="text.secondary">
              Memuat token video...
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Voice Call Dialog */}
      <VoiceCallDialog open={openVoiceCall} onClose={() => setOpenVoiceCall(false)} vehicle={selectedVoiceCallVehicle} token={token} />
    </Box>
  );
}
